import { useEffect, useState, type JSX } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useGuidance } from './api';
import { parseHelpParam } from './deep-link-parse';
import { conditionsHold, waitForQuiet } from './quiet';
import { openGuideHub, startTour } from './runtime';
import { DEEPLINK_POLL_MS, DEEPLINK_SETTLE_MAX_MS, QUIET_MS } from './timing';
import { useTourStore } from './tour-store';
import { TOURS } from './tours/registry';
import type { TourKey } from './tours/types';

interface PendingShellTour {
  key: TourKey;
  stepId?: string;
}

/**
 * Handles `?help=` on any shell route, then removes it with a `replace` —
 * which is not a route change, so it never interrupts a running tour.
 *
 *   ?help=hub                       opens the hub
 *   ?help=tour:<key>[.<stepId>]     starts or arms that tour (guide CTAs, the bell)
 *   ?help=tip:<itemKey>             opens the hub on «پیشنهادها», focused on that tip
 *
 * An unknown key, or a tour the viewer may not see, does nothing at all.
 *
 * A SHELL tour (the core tour) is not started in the mount effect: on the
 * first load after sign-in the shell is still settling there, and a tour
 * resolved against a half-mounted header skips the steps whose anchors are
 * not there yet. It is held until `GET /guidance` has answered, the welcome
 * has decided, and the page is quiet — the same gate the welcome waits for.
 * A page or inline tour starts (or arms) at once: its own place starts it.
 */
export function GuidanceDeepLinks(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const guidance = useGuidance();
  const [pending, setPending] = useState<PendingShellTour | null>(null);
  const stateKnown = Boolean(guidance.data) || guidance.isError;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get('help');
    if (value === null) return;
    params.delete('help');
    const search = params.toString();
    void navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state },
    );

    const link = parseHelpParam(value);
    if (!link) return;
    if (link.kind === 'hub') openGuideHub(undefined, 'deeplink');
    else if (link.kind === 'tip') openGuideHub({ section: 'tips', id: link.id }, 'deeplink');
    else if (TOURS[link.key].surface === 'shell')
      setPending({ key: link.key, stepId: link.stepId });
    else startTour(link.key, { source: 'deeplink', startAtStepId: link.stepId });
  }, [location.search, location.pathname, location.hash, location.state, navigate]);

  useEffect(() => {
    if (!pending || !stateKnown) return;
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      const startedAt = Date.now();
      const ready = (): boolean => {
        // The welcome decides first (it may open, and its «نمایش راهنما»
        // starts the core tour itself); past the deadline only the page matters.
        const welcomeSettled = useTourStore.getState().welcome === 'resolved';
        const late = Date.now() - startedAt > DEEPLINK_SETTLE_MAX_MS;
        return conditionsHold() && (welcomeSettled || late);
      };
      while (!ready()) {
        await new Promise((r) => setTimeout(r, DEEPLINK_POLL_MS));
        if (signal.aborted) return;
      }
      // The user asked for this tour: a short quiet moment, never a refusal.
      await waitForQuiet(QUIET_MS / 2, QUIET_MS * 2, signal);
      if (signal.aborted) return;
      setPending(null);
      if (useTourStore.getState().active?.key === pending.key) return;
      startTour(pending.key, { source: 'deeplink', startAtStepId: pending.stepId });
    })();
    return () => controller.abort();
  }, [pending, stateKnown]);

  return null;
}
